import { mediaService } from "@/services/media.service";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MDocumentCard from "@/components/ui/atoms/MDocumentCard";
import {
  IconInfoCircle,
  IconEdit,
  IconMessage2,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import { MButton } from "@/components/ui/atoms/MButtons";
import DocumentInfoDrawer from "./DocumentInfoDrawer";
import DocumentDrawer from "./DocumentDrawer";
import DocumentCommentDrawer from "./DocumentCommentDrawer";
import { useDocument } from "@/hooks/useDocument";
import { getMemberInfo } from "@/hooks/useMembers";
import { useGesture } from "@use-gesture/react";
import styles from "./document.module.css";
import PDFPreview from "./PDFPreview";

const DocumentViewer: React.FC = () => {
  const [infoDrawerOpen, setInfoDrawerOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const initialDocument = location.state?.document;
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [commentDrawerOpen, setCommentDrawerOpen] = React.useState(false);
  const [commentLoading, setCommentLoading] = React.useState(false);
  const [doc, setDoc] = React.useState(initialDocument);

  // Scroll to top when component mounts
  React.useEffect(() => {
    // Most reliable cross-browser approach - target all possible scroll containers
    const scrollToTop = () => {
      // Try all possible scroll containers
      window.scrollTo({ top: 0, behavior: "instant" });
      document.documentElement.scrollTo({ top: 0, behavior: "instant" });
      document.body.scrollTo({ top: 0, behavior: "instant" });

      // Also find any overflow:auto/scroll divs and reset them
      document
        .querySelectorAll(
          '[style*="overflow: auto"], [style*="overflow: scroll"], [class*="overflow"]'
        )
        .forEach((el) => {
          if (el instanceof HTMLElement) {
            el.scrollTop = 0;
          }
        });

      // Common scroll container selectors
      document
        .querySelectorAll(
          'div[style*="overflow-y: auto"], div[style*="overflow-y: scroll"], .overflow-y-auto, .overflow-y-scroll'
        )
        .forEach((el) => {
          if (el instanceof HTMLElement) {
            el.scrollTop = 0;
          }
        });
    };

    // Run immediately and again after a short delay to ensure DOM is ready
    scrollToTop();
    setTimeout(scrollToTop, 50);
  }, []);

  // Format date as "2nd Mar 2026"
  const formatDocumentDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString("en-US", { month: "short" });
      const year = date.getFullYear();

      // Add ordinal suffix (st, nd, rd, th)
      const getOrdinalSuffix = (n: number) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return s[(v - 20) % 10] || s[v] || s[0];
      };

      return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
    } catch {
      return dateString;
    }
  };

  // Get memberId for API — hoisted above early return so the useDocument hook
  // call below is unconditional (react-hooks/rules-of-hooks)
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const {
    updateDocumentFlag,
    downloadDocument,
    deleteDocument,
    updateDocumentNotes,
  } = useDocument(memberId);

  // Defensive fallback
  if (!doc) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <p>Document not found.</p>
        <button onClick={() => navigate("/documents/list")}>
          Back to List
        </button>
      </div>
    );
  }

  // Handlers
  const handleFlagChange = async (
    flag: "isImportant" | "isPrivate" | "availableOffline",
    value: boolean
  ) => {
    await updateDocumentFlag(doc, flag, value);
    setDoc((prev: any) => ({ ...prev, [flag]: value }));
  };
  const handleDownload = async () => {
    await downloadDocument(doc.media?.fileKey, doc.media?.fileName);
  };
  const handleDelete = async () => {
    await deleteDocument(doc);
    navigate("/documents/list");
  };
  const handleCommentClick = () => {
    setCommentDrawerOpen(true);
  };
  const handleCommentDone = async (value: string) => {
    setCommentLoading(true);
    try {
      await updateDocumentNotes(doc, value);
      setDoc((prev: any) => ({ ...prev, notes: value }));
    } finally {
      setCommentLoading(false);
      setCommentDrawerOpen(false);
    }
  };

  return (
    <div
      className="flex flex-col items-center w-full min-h-screen pt-8"
      style={{ paddingBottom: "calc(100px + env(safe-area-inset-bottom))" }}
    >
      {/* Document Card at the top */}
      <div className={styles.documentViewCard}>
        <div className="relative">
          <MDocumentCard
            cardType="document"
            category={doc.category}
            key={doc.id}
            displayName={doc.displayName || "Document"}
            insights={doc.insights || false}
            hideActions={true}
            meta={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>
                  Dated{" "}
                  {doc.documentDate ? formatDocumentDate(doc.documentDate) : ""}
                </span>
                <button
                  aria-label="Document Info"
                  onClick={(e) => {
                    e.stopPropagation();
                    setInfoDrawerOpen(true);
                  }}
                  type="button"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "#EDF3FC",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    borderRadius: "24px",
                  }}
                >
                  <IconInfoCircle
                    size={20}
                    stroke={1.6}
                    style={{ color: "#3263A3" }}
                  />
                </button>
              </div>
            }
            viewer
          />
        </div>
      </div>
      {/* Document Drawer */}
      <DocumentDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        displayName={doc.displayName || "Document"}
        fileKey={doc.media?.fileKey}
        fileName={doc.media?.fileName || doc.displayName || "Document"}
        isImportant={!!doc.isImportant}
        isPrivate={!!doc.isPrivate}
        availableOffline={!!doc.availableOffline}
        onImportantChange={(checked) =>
          handleFlagChange("isImportant", checked)
        }
        onPrivateChange={(checked) => handleFlagChange("isPrivate", checked)}
        onOfflineChange={(checked) =>
          handleFlagChange("availableOffline", checked)
        }
        onDownloadClick={handleDownload}
        onDelete={handleDelete}
        onCommentClick={handleCommentClick}
        commentButtonText={doc.notes ? "Edit Comment" : "Add Comment"}
      />
      {/* Info Drawer */}
      <DocumentInfoDrawer
        open={infoDrawerOpen}
        onClose={() => setInfoDrawerOpen(false)}
        fileName={doc.displayName || "Document"}
        size={doc.media?.size}
        uploadedAt={doc.media?.createdAt || doc.createdAt}
      />
      {/* Comment Drawer */}
      <DocumentCommentDrawer
        open={commentDrawerOpen}
        onClose={() => setCommentDrawerOpen(false)}
        initialValue={doc.notes || ""}
        onDone={handleCommentDone}
        loading={commentLoading}
        title="Edit Comment"
      />
      {/* Main Preview */}
      <div className="w-full max-w-2xl mx-auto">
        <DocumentPreviewInline
          memberId={memberId}
          fileKey={doc.media?.fileKey}
          mimeType={doc.media?.mimeType}
          fileName={doc.media?.fileName}
        />
      </div>
      {/* Comment section */}
      <div className="confirm-footer pt-[10px]">
        {doc.notes ? (
          <div className={styles.commentSection}>
            <div className={styles.commentHeader}>
              <span className="body-base-semibold default-text-color">
                Comment
              </span>
              <button
                className={styles.commentEditBtn}
                aria-label={doc.notes ? "Edit Comment" : "Add Comment"}
                onClick={() => setCommentDrawerOpen(true)}
                type="button"
              >
                <IconEdit size={20} stroke={1.6} color="#45168C" />
              </button>
            </div>
            <div className="body-xs-medium">{doc.notes}</div>
          </div>
        ) : (
          <MButton
            variant="outline"
            size="md"
            leftIcon={
              <IconMessage2
                size={16}
                style={{
                  color: "var(--brand-colours-indigo-indigo-400, #7046AE)",
                }}
              />
            }
            style={{
              width: "100%",
              borderRadius: 20,
              fontWeight: 500,
              color: "var(--brand-colours-indigo-indigo-400, #7046AE)",
              fontFamily: "var(--typography-font-family-base, Manrope)",
              fontSize: "var(--typography-font-size-body-s, 16px)",
            }}
            onClick={() => setCommentDrawerOpen(true)}
          >
            Add comment
          </MButton>
        )}
      </div>
    </div>
  );
};

const IMAGE_MIN_SCALE = 0.5;
const IMAGE_MAX_SCALE = 4;

// Inline DocumentPreview logic for blob download and preview
const DocumentPreviewInline: React.FC<{
  memberId: string;
  fileKey?: string;
  mimeType?: string;
  fileName?: string;
  className?: string;
}> = ({ memberId, fileKey, mimeType, fileName = "", className = "" }) => {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [, setPdfPageCount] = React.useState<number>(0);
  const [imageScale, setImageScale] = React.useState<number>(1.0);
  const imageContainerRef = React.useRef<HTMLDivElement>(null);
  const imageRef = React.useRef<HTMLImageElement>(null);
  const committedImageScaleRef = React.useRef<number>(1.0);

  const imgGestureRef = React.useRef<{
    startScale: number;
    startScrollLeft: number;
    startScrollTop: number;
    pinchCX: number;
    pinchCY: number;
  } | null>(null);
  // Scroll to restore after width reflow — applied in useLayoutEffect (before paint)
  const imgPendingScrollRef = React.useRef<{
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);

  // useLayoutEffect fires synchronously after DOM update but before browser paint —
  // ensures scroll is set after image width reflows, with no visible jump.
  React.useLayoutEffect(() => {
    const pending = imgPendingScrollRef.current;
    if (!pending) return;
    const container = imageContainerRef.current;
    const el = imageRef.current;
    if (container) {
      container.scrollLeft = pending.scrollLeft;
      container.scrollTop = pending.scrollTop;
    }
    if (el) {
      el.style.transform = "";
      el.style.transformOrigin = "";
    }
    imgPendingScrollRef.current = null;
  }, [imageScale]);

  const isImage = !!mimeType?.startsWith("image");

  const bind = useGesture(
    {
      onPinch: ({ offset: [s], origin: [ox, oy], first }) => {
        const el = imageRef.current;
        const container = imageContainerRef.current;
        if (!el || !container) return;

        if (first) {
          const containerRect = container.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          imgGestureRef.current = {
            startScale: committedImageScaleRef.current,
            startScrollLeft: container.scrollLeft,
            startScrollTop: container.scrollTop,
            pinchCX: ox - containerRect.left,
            pinchCY: oy - containerRect.top,
          };
          // Lock origin to pinch midpoint in element-local coords
          el.style.transformOrigin = `${ox - elRect.left}px ${
            oy - elRect.top
          }px`;
        }

        const g = imgGestureRef.current;
        if (!g) return;

        const newScale = Math.min(
          Math.max(s, IMAGE_MIN_SCALE),
          IMAGE_MAX_SCALE
        );
        // CSS transform ratio relative to the committed scale
        // (image width is already at committedScale * 100%, so we scale by ratio on top)
        const ratio = newScale / g.startScale;
        el.style.transform = `scale(${ratio})`;

        // Live scroll to keep pinch center stationary during gesture
        container.scrollLeft = Math.max(
          0,
          (g.startScrollLeft + g.pinchCX) * ratio - g.pinchCX
        );
        container.scrollTop = Math.max(
          0,
          (g.startScrollTop + g.pinchCY) * ratio - g.pinchCY
        );
      },

      onPinchEnd: ({ offset: [s] }) => {
        const finalScale = Math.min(
          Math.max(s, IMAGE_MIN_SCALE),
          IMAGE_MAX_SCALE
        );
        const g = imgGestureRef.current;

        if (g) {
          // Compute the scroll that matches the final zoom — applied after image reflows
          // (useLayoutEffect below handles this atomically before paint)
          const ratio = finalScale / g.startScale;
          imgPendingScrollRef.current = {
            scrollLeft: Math.max(
              0,
              (g.startScrollLeft + g.pinchCX) * ratio - g.pinchCX
            ),
            scrollTop: Math.max(
              0,
              (g.startScrollTop + g.pinchCY) * ratio - g.pinchCY
            ),
          };
          imgGestureRef.current = null;
        }

        committedImageScaleRef.current = finalScale;
        setImageScale(finalScale);
        // Note: CSS transform and transformOrigin are cleared in useLayoutEffect,
        // not here — so the visual is stable during the layout reflow.
      },

      onDrag: ({ delta: [dx, dy], tap }) => {
        if (tap || committedImageScaleRef.current <= 1.01) return;
        const container = imageContainerRef.current;
        if (!container) return;
        container.scrollLeft = Math.max(0, container.scrollLeft - dx);
        container.scrollTop = Math.max(0, container.scrollTop - dy);
      },
    },
    {
      pinch: {
        scaleBounds: { min: IMAGE_MIN_SCALE, max: IMAGE_MAX_SCALE },
        from: () => [committedImageScaleRef.current, 0],
        rubberband: true,
      },
      drag: { filterTaps: true },
    }
  );

  React.useEffect(() => {
    if (!fileKey) return;
    setPreviewUrl(null);
    setPreviewError(null);
    let url: string | null = null;
    const fetchPreview = async () => {
      try {
        const blob = await mediaService.download(memberId, fileKey);
        url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } catch (e) {
        setPreviewError("Failed to load preview.");
      }
    };
    fetchPreview();
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileKey]);

  if (!fileKey)
    return <span className="text-gray-400 text-base">No file to preview</span>;

  const handleImageZoomIn = () =>
    setImageScale((s) => {
      const next = Math.min(+(s + 0.25).toFixed(2), IMAGE_MAX_SCALE);
      committedImageScaleRef.current = next;
      return next;
    });
  const handleImageZoomOut = () =>
    setImageScale((s) => {
      const next = Math.max(+(s - 0.25).toFixed(2), IMAGE_MIN_SCALE);
      committedImageScaleRef.current = next;
      return next;
    });

  return (
    <>
      {/* Image zoom controls */}
      {previewUrl && isImage && (
        <div className={styles.overlayControlsContainer}>
          <div className={styles.zoomOverlay} style={{ visibility: "hidden" }}>
            <button
              className={
                imageScale > IMAGE_MIN_SCALE
                  ? styles.zoomBtn
                  : styles.zoomBtnDisabled
              }
              onClick={handleImageZoomOut}
              aria-label="Zoom out"
              disabled={imageScale <= IMAGE_MIN_SCALE}
            >
              <IconZoomOut size={22} />
            </button>
            <span className={styles.zoomPercent}>
              {Math.round(imageScale * 100)}%
            </span>
            <button
              className={
                imageScale < IMAGE_MAX_SCALE
                  ? styles.zoomBtn
                  : styles.zoomBtnDisabled
              }
              onClick={handleImageZoomIn}
              aria-label="Zoom in"
              disabled={imageScale >= IMAGE_MAX_SCALE}
            >
              <IconZoomIn size={22} />
            </button>
          </div>
        </div>
      )}

      <div
        className={`flex flex-col items-center justify-center min-h-[320px] ${className}`}
      >
        {!previewUrl && !previewError && (
          <span className="text-gray-400 text-base">Loading preview...</span>
        )}
        {previewError && (
          <span className="text-red-500 text-base">{previewError}</span>
        )}
        {previewUrl && isImage && (
          <div
            ref={imageContainerRef}
            className={styles.imageZoomContainer}
            style={{
              touchAction: "none",
              overflow: imageScale > 1 ? "auto" : "hidden",
            }}
            {...bind()}
          >
            <div className={styles.imageWrapper}>
              <img
                ref={imageRef}
                src={previewUrl}
                alt={fileName}
                className={`shadow ${styles.zoomableImage}`}
                style={{
                  width: `${imageScale * 100}%`,
                  willChange: "transform",
                }}
              />
            </div>
          </div>
        )}
        {previewUrl && mimeType === "application/pdf" && (
          <PDFPreview fileUrl={previewUrl} onLoadSuccess={setPdfPageCount} />
        )}
        {previewUrl && mimeType?.startsWith("video") && (
          <video
            controls
            src={previewUrl}
            className="w-full max-h-[65vh] shadow"
          />
        )}
        {/* Fallback for other types */}
        {previewUrl &&
          !isImage &&
          mimeType !== "application/pdf" &&
          !mimeType?.startsWith("video") && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline text-lg"
            >
              Open file
            </a>
          )}
      </div>
    </>
  );
};

export default DocumentViewer;
